import { ArrowUpRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { LAST_UPDATED_DISPLAY, LAST_UPDATED_ISO } from "@/constants";

// Dynamically imported so this page doesn't force vanilla-cookieconsent into
// its own chunk any earlier than main.tsx's own lazy init already does.
const openConsentPreferences = () => {
  import("@/lib/consent").then(({ showConsentPreferences }) => showConsentPreferences());
};

const cookies = [
  {
    name: "cc_cookie",
    provider: "Este sitio",
    duration: "6 meses",
    purpose: "Guarda tus preferencias de cookies (qué categorías aceptaste o rechazaste).",
  },
  {
    name: "_ga",
    provider: "Google Analytics",
    duration: "2 años",
    purpose: "Distingue a los usuarios únicos con un identificador generado al azar.",
  },
  {
    name: "_ga_*",
    provider: "Google Analytics",
    duration: "2 años",
    purpose: "Usada por Google Analytics 4 para mantener el estado de la sesión.",
  },
  {
    name: "_clck",
    provider: "Microsoft Clarity",
    duration: "1 año",
    purpose: "Guarda un identificador de usuario de Clarity y sus preferencias.",
  },
  {
    name: "_clsk",
    provider: "Microsoft Clarity",
    duration: "1 día",
    purpose: "Une las páginas vistas por una persona en una misma sesión.",
  },
  {
    name: "CLID",
    provider: "Microsoft Clarity (clarity.ms)",
    duration: "1 año",
    purpose:
      "Identifica la primera vez que Clarity vio a este usuario en cualquier sitio que usa Clarity.",
  },
  {
    name: "MUID",
    provider: "Microsoft (clarity.ms)",
    duration: "1 año",
    purpose: "Identifica navegadores únicos que visitan sitios de Microsoft.",
  },
  {
    name: "ANONCHK",
    provider: "Microsoft (clarity.ms)",
    duration: "10 minutos",
    purpose: "Indica si el MUID se transfiere a ANID.",
  },
  {
    name: "MR",
    provider: "Microsoft (clarity.ms)",
    duration: "7 días",
    purpose: "Indica si se debe actualizar el MUID.",
  },
  {
    name: "SM",
    provider: "Microsoft (c.clarity.ms)",
    duration: "Sesión",
    purpose: "Se usa para sincronizar el MUID entre dominios de Microsoft.",
  },
];

const browsers = [
  { label: "Chrome", href: "https://support.google.com/accounts/answer/61416?hl=es" },
  { label: "Firefox", href: "https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" },
  { label: "Safari", href: "https://support.apple.com/es-es/guide/safari/sfri11471/mac" },
  {
    label: "Microsoft Edge",
    href: "https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09",
  },
];

/** Cookie policy (DESIGN.md step 3b): same shell as the rest of the site. */
const CookiePolicy = () => (
  <SiteLayout>
    <section className="pq-wrap pq-intro pq-legal-intro">
      <nav aria-label="Ruta de navegación" className="pq-crumbs pq-rise">
        <a href="/">Inicio</a>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Política de cookies</span>
      </nav>
      <h1 className="pq-display pq-rise pq-rise-2">Política de cookies.</h1>
      <p className="pq-lead pq-rise pq-rise-3">
        Qué cookies usa este sitio, para qué sirven y cómo cambiar tus preferencias.
      </p>
      <p className="pq-meta pq-rise pq-rise-3">
        Última actualización: <time dateTime={LAST_UPDATED_ISO}>{LAST_UPDATED_DISPLAY}</time>
      </p>
    </section>

    <div className="pq-wrap pq-section pq-legal">
      <section aria-labelledby="que-h" className="pq-prose">
        <h2 id="que-h" className="pq-title">¿Qué son las cookies?</h2>
        <p>
          Las cookies son pequeños archivos de texto que un sitio web guarda en tu navegador
          cuando lo visitas. Sirven para recordar tus preferencias, o para entender cómo se usa
          el sitio.
        </p>
      </section>

      <section aria-labelledby="usamos-h" className="pq-legal-block">
        <div className="pq-prose">
          <h2 id="usamos-h" className="pq-title">Cookies que usamos.</h2>
          <p>
            En Camping Parque Tayrona solo usamos las cookies que se listan abajo. No vendemos ni
            compartimos tus datos con terceros con fines publicitarios.
          </p>
        </div>
        <ul className="pq-cookie-list">
          {cookies.map((c) => (
            <li key={c.name} className="pq-card">
              <code className="pq-cookie-name">{c.name}</code>
              <p className="pq-small">{c.purpose}</p>
              <dl className="pq-cookie-facts">
                <div>
                  <dt>Proveedor</dt>
                  <dd>{c.provider}</dd>
                </div>
                <div>
                  <dt>Duración</dt>
                  <dd>{c.duration}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="necesarias-h" className="pq-prose">
        <h2 id="necesarias-h" className="pq-title">Cookies necesarias.</h2>
        <p>
          Son las que permiten que el sitio funcione correctamente, como recordar tu elección de
          cookies. No se pueden desactivar porque el sitio no funcionaría sin ellas.
        </p>
      </section>

      <section aria-labelledby="analiticas-h" className="pq-prose">
        <h2 id="analiticas-h" className="pq-title">Cookies analíticas.</h2>
        <p>
          Usamos Google Analytics, a través de Google Tag Manager, para entender qué páginas se
          visitan más y cómo navegan las personas por el sitio. También usamos Microsoft Clarity,
          que graba de forma anónima cómo se usa cada página (clics, desplazamiento y mapas de
          calor) para ver qué partes de la guía son útiles. Clarity oculta por defecto lo que
          escribes en formularios. Esta información nos ayuda a mejorar el contenido de la guía.
        </p>
        <p>
          Nada de esto se carga sin tu permiso: ni Google Tag Manager ni Clarity se descargan, y no
          se registra nada, hasta que aceptas las cookies analíticas. Si después retiras tu
          consentimiento, dejan de funcionar y borramos sus cookies. Puedes leer cómo trata Microsoft estos datos en su{" "}
          <a
            href="https://www.microsoft.com/es-es/privacy/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
          >
            declaración de privacidad
          </a>
          .
        </p>
      </section>

      <section aria-labelledby="bloquear-h" className="pq-legal-block">
        <div className="pq-prose">
          <h2 id="bloquear-h" className="pq-title">Bloquear o eliminar cookies.</h2>
          <p>
            Puedes permitir, bloquear o eliminar las cookies en cualquier momento desde la
            configuración de tu navegador, o desde tus preferencias en este sitio. Si bloqueas la
            cookie necesaria, este aviso volverá a aparecer en cada visita.
          </p>
        </div>
        <ul className="pq-browser-list">
          {browsers.map((b) => (
            <li key={b.label}>
              <a href={b.href} target="_blank" rel="noopener noreferrer">
                <span>{b.label}</span>
                <span className="pq-sr"> (se abre en una pestaña nueva)</span>
                <ArrowUpRight size={18} aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="cambiar-h" className="pq-card pq-legal-cta">
        <h2 id="cambiar-h" className="pq-title">Cambia tus preferencias.</h2>
        <p className="pq-small">
          Acepta, rechaza o ajusta las cookies analíticas cuando quieras, desde aquí o desde
          "Gestionar cookies" en el pie de cualquier página.
        </p>
        <button type="button" onClick={openConsentPreferences} className="pq-btn pq-btn-primary">
          Gestionar cookies
        </button>
      </section>
    </div>
  </SiteLayout>
);

export default CookiePolicy;
